/** @jsxImportSource @emotion/react */
import { withMyTheme } from "../../../theme/theme"
import { css, Typography, Box, Switch, FormControlLabel } from "@mui/material"
import { Add } from "@mui/icons-material"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { mobileCss } from "../../../util/isMobile"
import { useQuestionnaireForm } from "../context/QuestionnaireFormContext"
import { MenuSection } from "../../common/context/SharedFormTypes"
import { MenuSectionDialog } from "./components/MenuSectionDialog"
import { MenuSectionCard } from "./components/MenuSectionCard"
import { MyButton } from "../../../components/button/MyButton"

const StepTitleStyle = withMyTheme((theme) => css`
    font-size: 2rem;
    font-weight: 600;
    color: ${theme.palette.primary.main};
    text-align: center;
    margin-bottom: 1rem;

    ${mobileCss(`
        font-size: 1.5rem;
    `)}
`)

const StepContentStyle = withMyTheme(() => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5vh;
    text-align: center;
    margin: 0 auto;
    padding: 0 1rem;

    ${mobileCss(`
        max-width: 90vw;
        gap: 1.5rem;
    `)}
`)

const ToggleContainerStyle = withMyTheme(() => css`
    display: flex;
    justify-content: center;
    margin-bottom: 0.5rem;
`)

const ToggleLabelStyle = withMyTheme((theme) => css`
    .MuiFormControlLabel-label {
        font-family: ${theme.typography.body1.fontFamily};
        font-size: 1.1rem;
        font-weight: 500;
        color: ${theme.palette.text.primary};
    }
`)

const SwitchStyle = withMyTheme((theme) => css`
    & .MuiSwitch-switchBase.Mui-checked {
        color: ${theme.palette.primary.main};
    }
    & .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track {
        background-color: ${theme.palette.primary.main};
    }
`)

const StayTunedToggleStyle = withMyTheme((theme) => css`
    .MuiFormControlLabel-label {
        font-family: ${theme.typography.body1.fontFamily};
        font-size: 0.95rem;
        color: ${theme.palette.text.secondary};
    }
`)

const SectionsContainerStyle = withMyTheme(() => css`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    width: 50vw;
    margin-top: 0.5rem;

    ${mobileCss(`
        width: 85vw;
    `)}
`)

const AddButtonWrapperStyle = withMyTheme(() => css`
    display: flex;
    justify-content: center;
    margin-top: 0.5rem;
`)

const StayTunedDescStyle = withMyTheme((theme) => css`
    font-size: 0.85rem;
    color: ${theme.palette.text.secondary};
    font-style: italic;
    max-width: 400px;
`)

interface MenuStepProps {
    onStart?: () => void
}

export const MenuStep = ({}: MenuStepProps) => {
    const { t } = useTranslation()
    const { formData, updateMenu } = useQuestionnaireForm()
    const [dialogOpen, setDialogOpen] = useState(false)
    const [editingSection, setEditingSection] = useState<MenuSection | null>(null)

    const menu = formData.menu ?? { enabled: false, stayTuned: false, sections: [] }
    const primaryColor = formData.colors?.primaryColor || '#C45526'
    const isEnabled = menu.enabled !== false

    const handleEnableToggle = () => {
        updateMenu({ enabled: !isEnabled })
    }

    const handleStayTunedToggle = () => {
        updateMenu({ stayTuned: !menu.stayTuned })
    }

    const handleAddSection = () => {
        setEditingSection(null)
        setDialogOpen(true)
    }

    const handleEditSection = (section: MenuSection) => {
        setEditingSection(section)
        setDialogOpen(true)
    }

    const handleSaveSection = (section: MenuSection) => {
        const existing = menu.sections.find(s => s.id === section.id)
        const updatedSections = existing
            ? menu.sections.map(s => s.id === section.id ? section : s)
            : [...menu.sections, section]
        updateMenu({ sections: updatedSections })
    }

    const handleDeleteSection = (id: string) => {
        updateMenu({ sections: menu.sections.filter(s => s.id !== id) })
    }

    return (
        <div css={StepContentStyle}>
            <Typography css={StepTitleStyle}>
                {t('questionnaire.menu.title')}
            </Typography>

            <Box css={ToggleContainerStyle}>
                <FormControlLabel
                    css={ToggleLabelStyle}
                    control={
                        <Switch
                            css={SwitchStyle}
                            checked={isEnabled}
                            onChange={handleEnableToggle}
                        />
                    }
                    label={t('questionnaire.menu.enableToggle')}
                />
            </Box>

            {isEnabled && (
                <>
                    <Box css={ToggleContainerStyle}>
                        <FormControlLabel
                            css={StayTunedToggleStyle}
                            control={
                                <Switch
                                    css={SwitchStyle}
                                    checked={menu.stayTuned}
                                    onChange={handleStayTunedToggle}
                                    size="small"
                                />
                            }
                            label={t('questionnaire.menu.stayTunedToggle')}
                        />
                    </Box>
                    {menu.stayTuned && (
                        <Typography css={StayTunedDescStyle}>
                            {t('questionnaire.menu.stayTunedDescription')}
                        </Typography>
                    )}

                    <Box css={SectionsContainerStyle}>
                        {menu.sections.map(section => (
                            <MenuSectionCard
                                key={section.id}
                                section={section}
                                primaryColor={primaryColor}
                                onClick={() => handleEditSection(section)}
                                onDelete={() => handleDeleteSection(section.id)}
                            />
                        ))}
                    </Box>

                    <Box css={AddButtonWrapperStyle}>
                        <MyButton
                            variant="primary"
                            text={t('questionnaire.menu.addSection')}
                            startIcon={<Add />}
                            onClick={handleAddSection}
                        />
                    </Box>

                    <MenuSectionDialog
                        open={dialogOpen}
                        section={editingSection}
                        primaryColor={primaryColor}
                        onSave={handleSaveSection}
                        onClose={() => setDialogOpen(false)}
                    />
                </>
            )}
        </div>
    )
}
